import { AUTH_TOKEN } from 'app/constants';
import {App, TradeType, Offer, PaginationResponseType} from 'app/console-api';
import { HttpError, HttpErrorProps } from 'app/components/utils/http-error';
import { PaginationParams } from 'app/console-api/utils/pagination';
import { RewardsValue } from 'app/console-api/apps/types';
import { UrlFilterController } from 'app/components/filters/url-filters';
import { CategoriesAdapter } from 'app/components/filters/categories-filter';
import { DealsStatusAdapter } from 'app/components/filters/deals-status-filter';
import { DealStatus } from 'app/components/common/deals-statuses/deals-statuses';
import { AutomationRequest } from 'app/console-api/automations/types';
import { DealTypesAdapter } from 'app/components/filters/deal-types-filter';

export enum DealRequestType {
  Outgoing = 'outgoing',
  Incoming = 'incoming'
}

export interface DealDetails {
  id: string;
  relationID: string;
  organizationID: string;
  tradeType: TradeType;
  requestType: DealRequestType;
  title: string;
  customMetadata: object | null;
  appID: string;
  offerID: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface DealStats {
  claimedIn: number;
  claimedOut: number;
}

export interface CreateDealRequest {
  tradeType: TradeType;
  myOfferID?: string;
  targetOfferID: string;
  appID: string;
  title: string;
  customMetadata: object | null;
  rewards: RewardsValue[];
  categories: number[];
  automations: AutomationRequest[];
}

export interface UpdateDealRequest {
  dealStatus: DealStatus;
  statusDescription?: string;
  title: string;
  customMetadata: object | null;
  categories: number[];
  rewards: RewardsValue[];
}

export interface DealListItemResponse {
  dealStatus: DealStatus;
  statusDescription: string;
  dealDetails: DealDetails;
  myApp: App;
  myOffer: Offer | null;
  targetOffer: Offer | null;
  targetApp: App;
  stats: DealStats;
  categories: string[];
  rewards: RewardsValue[];
  automations: AutomationRequest[];
}

export const requestDeal = async (data: CreateDealRequest): Promise<DealDetails> => {
  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps/${data.appID}/deals`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN) || ''}`
      },
      body: JSON.stringify(data)
    }
  );
  if (!response.ok) {
    if (response.body) {
      return response
        .clone()
        .json()
        .then((errorInfo: HttpErrorProps) => {
          return Promise.reject(
            new HttpError(
              `Failed to request deal: ${response.statusText}`,
              response.status,
              errorInfo
            )
          );
        });
    } else {
      return Promise.reject(
        new HttpError(`Failed to request deal: ${response.statusText}`, response.status)
      );
    }
  }
  return await response.json();
};

export const getMyDeals = async (
  appID: string,
  ctl?: UrlFilterController,
  params?: PaginationParams
): Promise<PaginationResponseType<DealListItemResponse>> => {
  let searchParams = new URLSearchParams();
  if (ctl) {
    searchParams = ctl.buildSearchParams([
      { adapter: DealsStatusAdapter, value: ctl.get(DealsStatusAdapter) },
      { adapter: CategoriesAdapter, value: ctl.get(CategoriesAdapter) },
      { adapter: DealTypesAdapter, value: ctl.get(DealTypesAdapter) }
    ]);
  }
  if (!params) {
    params = {
      pageSize: Number(process.env.REACT_APP_PAGE_SIZE_ITEMS),
      currentPage: 1
    };
  }
  searchParams.append('pageSize', params.pageSize.toString());
  searchParams.append('currentPage', params.currentPage.toString());

  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps/${appID}/deals?${searchParams.toString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN) || ''}`
      }
    }
  );
  if (!response.ok) {
    return Promise.reject(
      new HttpError(`Failed to get deals: ${response.statusText}`, response.status)
    );
  }
  return await response.json();
};

export const getDealByID = async (appID: string, dealID: string): Promise<DealListItemResponse> => {
  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps/${appID}/deals/${dealID}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN) || ''}`
      }
    }
  );
  if (!response.ok) {
    return Promise.reject(
      new HttpError(`Failed to get deals: ${response.statusText}`, response.status)
    );
  }
  return await response.json();
};

export const updateDeal = async (
  appID: string,
  dealID: string,
  data: UpdateDealRequest
): Promise<DealDetails> => {
  const response = await fetch(
    `${process.env.REACT_APP_TRAFFIC_EXCHANGE_CONSOLE_API_URL}/apps/${appID}/deals/${dealID}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN) || ''}`
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    if (response.body) {
      return response
        .clone()
        .json()
        .then((errorInfo: HttpErrorProps) => {
          return Promise.reject(
            new HttpError(
              `Failed to update deal: ${response.statusText}`,
              response.status,
              errorInfo
            )
          );
        });
    } else {
      return Promise.reject(
        new HttpError(`Failed to update deal: ${response.statusText}`, response.status)
      );
    }
  }

  return await response.json();
};
